import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type {
  CanonicalPresentationCondition,
  StudioDepartureKitStatusV1,
  StudioProjectLifecycleSummaryV1,
} from "@enrein/shared-contracts";
import { derivePairedChangeRequestPresentation } from "@enrein/shared-contracts";
import type { StudioProjectSummaryV1, StudioRunRowV1 } from "@enrein/shared-contracts/studio-dashboard";
import type { AgencyPublicScanOpportunityV1 } from "@enrein/shared-contracts/contracts/agency-unlock";
import {
  deriveGuidedNextActions,
  deriveSimpleProjectPhase,
  humanRunStatusLabel,
  isHandoffReadyDepartureKit,
  isQaFailureRunStatus,
  resolveStudioHasPreview,
  type SimpleProjectPhaseId,
  type SimpleProjectPhaseState,
} from "../../studio-shared/orchestration/project-phase.js";
import type { V10ControlRoomSection, V10ProjectMode } from "./v10-phase-model.js";
import { V10_CONTROL_ROOM_SECTIONS } from "./v10-phase-model.js";
import type { V10InboxRow, V10ProjectListItem, V10QueueCard, V10ScanRow } from "./v10-fixtures.js";
import { deriveV10QueueRisk } from "./v10-queue-risk.js";
import { deriveV10PipelineDueLabel } from "./v10-pipeline-filters.js";

/** Agency inbox labels from shared canonical conditions — never recompute from raw lifecycle status. */
function agencyInboxStatusFromCondition(condition: CanonicalPresentationCondition): string {
  switch (condition) {
    case "READY":
      return "Awaiting agency start";
    case "IN_PROGRESS":
      return "Agency working";
    case "COMPLETE":
      return "Revised preview ready";
    case "WAITING":
      return "Waiting";
    case "NEEDS_ATTENTION":
      return "Needs attention";
  }
}

const UI_ACTIVE_GENERATION_STATUSES = [
  "PENDING",
  "GENERATING",
  "GENERATED",
  "QA_QUEUED",
  "QA_RUNNING",
] as const;

export type V10SectionProgressStatus = "done" | "current" | "upcoming";

export function resolveV10IntakeSubmitted(input: {
  readonly intakeSummary?: { readonly status?: string } | null;
  readonly review?: ClientReviewStatusV1 | null;
}): boolean {
  return (
    input.intakeSummary?.status === "SUBMITTED" ||
    input.review?.clientLink?.status === "INTAKE_SUBMITTED"
  );
}

export function stubRunsFromSummary(project: StudioProjectSummaryV1): readonly StudioRunRowV1[] {
  const status = project.lastKnownRunStatus;
  if (!(UI_ACTIVE_GENERATION_STATUSES as readonly string[]).includes(status)) {
    return [];
  }
  const timestamp = project.lastRunUpdatedAt;
  return [
    {
      schemaId: "studio.runRow.v1",
      agencyId: project.agencyId,
      projectId: project.projectId,
      runId: `run_stub_${project.projectId}` as StudioRunRowV1["runId"],
      status,
      createdAt: timestamp,
      updatedAt: timestamp,
      recipeId: "recipe-portfolio-stub",
      qaRollup: "PENDING",
      artifactSetHashPrefix: "",
    },
  ];
}

function toV10Mode(mode: string | undefined | null): V10ProjectMode {
  if (mode === "EXISTING_SITE_SCAN" || mode === "EXTERNAL_PROOF_ONLY") return mode;
  return "DEFAULT_GENERATION";
}

export function resolveProjectDisplayName(input: {
  readonly projectId: string;
  readonly businessName?: string | null;
  readonly review?: ClientReviewStatusV1 | null;
}): string {
  return (
    input.businessName?.trim() ||
    input.review?.clientLink?.clientDisplayName?.trim() ||
    input.projectId.replace(/^project_/, "").replace(/_/g, " ") ||
    "Client"
  );
}

function isAwaitingClientIntake(status: string | undefined): boolean {
  if (!status) return false;
  return status.startsWith("INTAKE") && status !== "INTAKE_SUBMITTED";
}

export function phaseToControlRoomSection(phase: SimpleProjectPhaseId): V10ControlRoomSection {
  switch (phase) {
    case "waiting_for_client":
      return "overview";
    case "review_and_build":
      return "business";
    case "generating":
      return "website";
    case "preview_live":
      return "website";
    case "client_approved":
      return "approval-delivery";
    case "packaging":
      return "approval-delivery";
    case "handoff_ready":
      return "approval-delivery";
    default:
      return "overview";
  }
}

export function deriveV10SuggestedSection(
  phase: SimpleProjectPhaseId,
  review: ClientReviewStatusV1 | null | undefined,
): V10ControlRoomSection {
  if (review?.latestChangeRequest) {
    return "changes";
  }
  if (review?.latestPreview && !review.latestApproval) {
    return "website";
  }
  return phaseToControlRoomSection(phase);
}

export function deriveV10EngineCurrentSection(
  phase: SimpleProjectPhaseId,
  review: ClientReviewStatusV1 | null | undefined,
): V10ControlRoomSection {
  if (phase === "handoff_ready") {
    return "overview";
  }
  if (review?.latestChangeRequest) {
    return "changes";
  }
  if (review?.latestPreview && !review.latestApproval) {
    return "approval-delivery";
  }
  return phaseToControlRoomSection(phase);
}

export function deriveV10SectionProgress(
  phaseState: Pick<SimpleProjectPhaseState, "phase">,
  review: ClientReviewStatusV1 | null | undefined,
): Record<V10ControlRoomSection, V10SectionProgressStatus> {
  const current = deriveV10EngineCurrentSection(phaseState.phase, review);
  const currentIndex = V10_CONTROL_ROOM_SECTIONS.indexOf(current);
  return Object.fromEntries(
    V10_CONTROL_ROOM_SECTIONS.map((id, index) => {
      if (index < currentIndex) return [id, "done" as const];
      if (index === currentIndex) return [id, "current" as const];
      return [id, "upcoming" as const];
    }),
  ) as Record<V10ControlRoomSection, V10SectionProgressStatus>;
}

export interface V10PanelBlocks {
  readonly setup: boolean;
  readonly facts: boolean;
  readonly preview: boolean;
  readonly l2Edits: boolean;
  readonly approval: boolean;
  readonly handoff: boolean;
  readonly liveCare: boolean;
}

export function resolveV10PanelBlocks(input: {
  readonly section: V10ControlRoomSection;
  readonly phaseState: SimpleProjectPhaseState | null;
  readonly changeRequestPreview: string | null;
}): V10PanelBlocks {
  const { section, phaseState, changeRequestPreview } = input;
  const phase = phaseState?.phase;
  return {
    // setup + live-care both alias to overview; phase separates panel ownership
    setup: (section === "overview" && phase !== "handoff_ready") || phase === "waiting_for_client",
    facts: section === "business" || phaseState?.showFactsPanel === true,
    preview:
      section === "website" ||
      (section === "approval-delivery" && phaseState?.showPreviewPanel === true) ||
      (phaseState?.showPreviewPanel === true && !(section === "overview" && phase === "handoff_ready")),
    l2Edits: section === "changes" || Boolean(changeRequestPreview),
    approval:
      section === "approval-delivery" ||
      (phase === "preview_live" && phaseState?.showHandoffPanel !== true && !changeRequestPreview),
    handoff: section === "approval-delivery" || phaseState?.showHandoffPanel === true,
    liveCare: phase === "handoff_ready",
  };
}

function deriveProjectGroup(input: {
  readonly project: StudioProjectSummaryV1;
  readonly review: ClientReviewStatusV1 | null | undefined;
  readonly departureKit: StudioDepartureKitStatusV1 | null | undefined;
}): V10ProjectListItem["group"] {
  const { project, review, departureKit } = input;
  if (isHandoffReadyDepartureKit(departureKit)) return "handoff-ready";
  if (isQaFailureRunStatus(project.lastKnownRunStatus)) return "blocked";
  const awaitingClient = isAwaitingClientIntake(review?.clientLink?.status);
  if (awaitingClient) return "waiting-client";
  if (!project.currentBusinessGraphRevisionId && !review?.latestPreview) return "draft";
  return "active";
}

function formatAgeLabel(isoDate: string | undefined): string {
  if (!isoDate) return "Recently";
  const ms = Date.now() - new Date(isoDate).getTime();
  if (Number.isNaN(ms) || ms < 0) return "Recently";
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1d ago" : `${days}d ago`;
}

export function mapProjectSummaryToListItem(input: {
  readonly project: StudioProjectSummaryV1;
  readonly businessName: string | null;
  readonly review: ClientReviewStatusV1 | null | undefined;
  readonly departureKit: StudioDepartureKitStatusV1 | null | undefined;
  readonly lifecycleSummary?: StudioProjectLifecycleSummaryV1 | null;
  readonly ownerLabel?: string | null;
}): V10ProjectListItem {
  const name = resolveProjectDisplayName({
    projectId: input.project.projectId,
    businessName: input.businessName,
    review: input.review,
  });
  const group = deriveProjectGroup(input);
  const statusLabel = input.project.currentBusinessGraphRevisionId
    ? "Business Profile active"
    : humanRunStatusLabel(input.project.lastKnownRunStatus);
  const phase = input.lifecycleSummary?.simplePhase ?? "waiting_for_client";
  const section = deriveV10SuggestedSection(phase, input.review);
  const qaFailure = isQaFailureRunStatus(input.project.lastKnownRunStatus);
  const awaitingClient = isAwaitingClientIntake(input.review?.clientLink?.status);
  const ownerLabel =
    input.ownerLabel?.trim() ||
    input.lifecycleSummary?.ownerLabel?.trim() ||
    "Agency owner";
  const riskLevel = deriveV10QueueRisk({
    qaFailure,
    hasChangeRequest: Boolean(input.review?.latestChangeRequest),
    awaitingClient,
    handoffReady: isHandoffReadyDepartureKit(input.departureKit),
  });
  const outstandingClientAction =
    Boolean(input.review?.latestChangeRequest) ||
    awaitingClient ||
    (Boolean(input.review?.latestPreview) && !input.review?.latestApproval);
  return {
    id: input.project.projectId,
    name,
    summary: statusLabel,
    pills: [statusLabel],
    ageLabel: formatAgeLabel(input.project.lastRunUpdatedAt),
    group,
    currentSection: section,
    mode: toV10Mode(input.project.projectMode),
    ownerLabel,
    riskLevel,
    dueLabel: deriveV10PipelineDueLabel(group),
    outstandingClientAction,
  };
}

export function deriveV10QueueSections(input: {
  readonly projects: readonly StudioProjectSummaryV1[];
  readonly reviews: Readonly<Record<string, ClientReviewStatusV1 | null>>;
  readonly departureKits: Readonly<Record<string, StudioDepartureKitStatusV1 | null>>;
  readonly businessNames: Readonly<Record<string, string | null>>;
  readonly ownerLabels?: Readonly<Record<string, string | null>>;
  readonly lifecycleSummaries?: Readonly<Record<string, StudioProjectLifecycleSummaryV1 | null>>;
}): {
  readonly continueCards: readonly V10QueueCard[];
  readonly attentionCards: readonly V10QueueCard[];
  readonly clientRequestCards: readonly V10QueueCard[];
  readonly stuckCards: readonly V10QueueCard[];
  readonly readyCards: readonly V10QueueCard[];
} {
  const portfolio = input.projects.map((project) => ({
    projectId: project.projectId,
    businessName: input.businessNames[project.projectId] ?? null,
    lastKnownRunStatus: project.lastKnownRunStatus,
  }));
  const allActions = deriveGuidedNextActions({
    projects: portfolio,
    reviews: input.reviews,
    departureKits: input.departureKits,
    limit: null,
  });

  const continueCards: V10QueueCard[] = [];
  const attentionCards: V10QueueCard[] = [];
  const clientRequestCards: V10QueueCard[] = [];
  const stuckCards: V10QueueCard[] = [];
  const readyCards: V10QueueCard[] = [];

  for (const action of allActions) {
    const review = input.reviews[action.projectId];
    const dk = input.departureKits[action.projectId];
    const project = input.projects.find((p) => p.projectId === action.projectId);
    const section = input.lifecycleSummaries?.[action.projectId]?.simplePhase
      ? deriveV10SuggestedSection(input.lifecycleSummaries[action.projectId]!.simplePhase, review)
      : project
        ? deriveV10SuggestedSection(
            deriveSimpleProjectPhase({
              intakeSummary: null,
              hasActiveRevision: Boolean(project.currentBusinessGraphRevisionId),
              intakeSubmitted: resolveV10IntakeSubmitted({ review }),
              hasPreview: resolveStudioHasPreview({ clientReview: review, runs: stubRunsFromSummary(project) }),
              runs: stubRunsFromSummary(project),
              clientReview: review,
              departureKit: dk,
            }).phase,
            review,
          )
        : "overview";
    const ownerLabel =
      input.ownerLabels?.[action.projectId]?.trim() ||
      input.lifecycleSummaries?.[action.projectId]?.ownerLabel?.trim() ||
      "Agency owner";
    const qaFailure = isQaFailureRunStatus(project?.lastKnownRunStatus);
    const awaitingClient = isAwaitingClientIntake(review?.clientLink?.status);
    const riskLevel = deriveV10QueueRisk({
      qaFailure,
      hasChangeRequest: Boolean(review?.latestChangeRequest),
      awaitingClient,
      handoffReady: isHandoffReadyDepartureKit(dk),
    });
    const card: V10QueueCard = {
      id: `queue-${action.projectId}`,
      clientName: action.clientName,
      happened: action.title,
      blocked: action.why,
      ownerLabel,
      riskLevel,
      ageLabel: "Now",
      ctaLabel: action.cta,
      projectId: action.projectId,
      targetSection: section,
      mode: toV10Mode(project?.projectMode ?? "DEFAULT_GENERATION"),
    };
    if (isHandoffReadyDepartureKit(dk)) {
      readyCards.push(card);
    } else if (isQaFailureRunStatus(project?.lastKnownRunStatus)) {
      stuckCards.push(card);
    } else if (review?.latestChangeRequest) {
      clientRequestCards.push(card);
    } else if (review?.latestPreview && !review.latestApproval) {
      attentionCards.push(card);
    } else {
      continueCards.push(card);
    }
  }

  return { continueCards, attentionCards, clientRequestCards, stuckCards, readyCards };
}

export function deriveV10InboxRows(input: {
  readonly projects: readonly StudioProjectSummaryV1[];
  readonly reviews: Readonly<Record<string, ClientReviewStatusV1 | null>>;
  readonly businessNames: Readonly<Record<string, string | null>>;
}): readonly V10InboxRow[] {
  const rows: V10InboxRow[] = [];
  for (const project of input.projects) {
    const review = input.reviews[project.projectId];
    if (!review) continue;
    const clientName = resolveProjectDisplayName({
      projectId: project.projectId,
      businessName: input.businessNames[project.projectId],
      review,
    });
    const projectName = clientName;
    const mode = toV10Mode(project.projectMode);
    if (review.latestChangeRequest) {
      const cr = review.latestChangeRequest;
      const paired = derivePairedChangeRequestPresentation({
        projectId: project.projectId,
        changeRequestId: cr.changeRequestId,
        status: cr.status,
        messagePreview: cr.messagePreview,
        createdAt: cr.createdAt,
        startedAt: cr.startedAt,
        resolvedAt: cr.resolvedAt,
        resolvedPreviewVersionId: cr.resolvedPreviewVersionId,
        resolutionMatchesCurrentPassingArtifact: cr.resolutionMatchesCurrentPassingArtifact,
        latestPreviewVersionId: review.latestPreview?.previewVersionId,
      });
      if (!paired) continue;
      const agency = paired.agency;
      rows.push({
        id: `inbox-change-${project.projectId}`,
        clientName,
        projectName,
        projectId: project.projectId,
        requestType: "change-request",
        requestLabel: "Change request",
        status: agencyInboxStatusFromCondition(agency.condition),
        ageLabel: "Recent",
        ctaLabel: agency.primaryAction?.label ?? "Review changes",
        targetSection: "changes",
        mode,
      });
    }
    if (review.clientLink?.status === "INTAKE_SUBMITTED") {
      rows.push({
        id: `inbox-intake-submitted-${project.projectId}`,
        clientName,
        projectName,
        projectId: project.projectId,
        requestType: "missing-info",
        requestLabel: "Intake submitted",
        status: "Ready to build preview",
        ageLabel: "Recent",
        ctaLabel: "Review facts",
        targetSection: "business",
        mode,
      });
    } else if (isAwaitingClientIntake(review.clientLink?.status)) {
      rows.push({
        id: `inbox-intake-${project.projectId}`,
        clientName,
        projectName,
        projectId: project.projectId,
        requestType: "missing-info",
        requestLabel: "Intake pending",
        status: "Waiting for client",
        ageLabel: "Open",
        ctaLabel: "Open setup",
        targetSection: "overview",
        mode,
      });
    }
    if (review.latestPreview && !review.latestApproval && !review.latestChangeRequest) {
      rows.push({
        id: `inbox-approval-${project.projectId}`,
        clientName,
        projectName,
        projectId: project.projectId,
        requestType: "approval-pending",
        requestLabel: "Preview shared",
        status: "Waiting for client decision",
        ageLabel: "Recent",
        ctaLabel: "Open review",
        targetSection: "website",
        mode,
      });
    }
  }
  return rows;
}

export function mapScanOpportunityToRow(opportunity: AgencyPublicScanOpportunityV1): V10ScanRow {
  return {
    id: opportunity.claimId,
    url: opportunity.scannedUrl,
    label: opportunity.scannedUrl.replace(/^https?:\/\//, "").slice(0, 48),
    ageLabel: formatAgeLabel(opportunity.claimedAt),
    projectId: null,
    status: "Claimed",
  };
}

// Re-export for adapters that need handoff check
export { isHandoffReadyDepartureKit } from "../../studio-shared/orchestration/project-phase.js";
